import React, { useEffect } from 'react';
import { Form, useForm } from 'react-final-form';
import { Localizer } from 'services/Localizer';
import { ServerData } from 'services/ServerData';
import Spinner from 'shared/components/Spinner';
import { TextBoxField } from 'shared/edit/fields';
import ReCaptchaDisclaimer from 'shared/edit/fields/ReCaptchaDisclaimer';
import InlineAlert from 'shared/edit/InlineAlert';
import { useRocFormSubmitter } from 'shared/hooks/useBaseAxiosFormSubmitter';
import OrderDetails from '../order-details/OrderDetails';
import { OrderDetailsResult } from '../order-details/types';

export interface OrderStatusRequest {
	orderNumber: string;
	// #region HCL
	//billingZipCode: string;
	shippingZipCode: string;
	// #endregion
}

interface OrderStatusFormProps {
	handleSubmit: any;
	submitting: boolean;
	responseData: OrderDetailsResult | null;
}

/**
 * Order Status for looking up the details of an order
 */
export default function OrderStatus() {
	/**
	 * The POST request that is executed when submitting the OrderStatus form.
	 */
	const orderStatusFormSubmitter = useRocFormSubmitter<OrderStatusRequest, OrderDetailsResult>({
		axiosParams: { url: '/ajax/order-status' },
		reCaptchaActionName: 'OrderStatus',
		invalidateTokenOnSuccess: true,
	});

	return (
		<div className="roc-container">
			<Form<OrderStatusRequest>
				onSubmit={orderStatusFormSubmitter.handleSubmit}
				subscription={{
					submitError: true,
					submitting: true,
					submitSucceeded: true,
				}}
				render={({ submitting, handleSubmit }) => (
					<OrderStatusForm
						handleSubmit={handleSubmit}
						submitting={submitting}
						responseData={orderStatusFormSubmitter.responseData}
					/>
				)}
			/>
		</div>
	);
}

/**
 * Inner markup for OrderStatus component which includes either a form for looking up an order,
 * or the details of an order which has been looked up.
 */
function OrderStatusForm(props: OrderStatusFormProps) {
	const { submitting, handleSubmit, responseData } = props;
	const form = useForm();
	const { submitSucceeded, submitError } = form.getState();

	useEffect(() => {
		const queryParams = new URLSearchParams(location.search);

		// #region HCL
		const orderNo = queryParams.get('orderNumber');
		//const billingZip = queryParams.get('billingZip');
		const shippingZip = queryParams.get('shippingZipCode');
		// #endregion

		if (orderNo && shippingZip) {
			form.batch(() => {
				form.change('orderNumber', orderNo);
				// #region HCL
				//form.change('billingZipCode', billingZip);
				form.change('shippingZipCode', shippingZip);
				// #endregion
			});

			form.submit();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			{!submitSucceeded ? (
				<>
					<h1>{Localizer('OrderStatus')}</h1>

					{submitError ? <InlineAlert message={submitError} error={null} /> : null}
					{/* #region HCL */}
					<form
						onSubmit={() => {
							handleSubmit();
						}}
					>
						{/* #endregion  */}
						<div className="roc-row">
							<div className="roc-col-md-4">
								{/* #region HCL */}
								<TextBoxField label={Localizer('WebReferenceOrOrderNumber')} name="orderNumber" />
								{/* #endregion  */}
							</div>
							<div className="roc-col-md-4">
								{/* #region HCL */}
								{/* <TextBoxField label={Localizer('BillingZipCode')} name="billingZipCode" /> */}
								<TextBoxField label={Localizer('ShippingZipCode')} name="shippingZipCode" />
								{/* #endregion  */}
							</div>
						</div>
						<ReCaptchaDisclaimer isEnabled={ServerData.IsOrderStatusReCaptchaEnabled} />
						<button
							disabled={submitting}
							type="submit"
							className="roc-btn"
							data-testid="order-status-form-submit"
						>
							{Localizer('LookUpOrder')} {submitting ? <Spinner light /> : null}
						</button>
					</form>
				</>
			) : (
				<>
					<button onClick={() => form.reset()} className="roc-btn" data-testid="order-status-form-reset">
						{Localizer('LookUpAnotherOrder')}
					</button>
					{responseData ? (
						<OrderDetails orderDetails={responseData} />
					) : (
						<InlineAlert message={Localizer('OrderDetailsError')} error={null} />
					)}
				</>
			)}
		</>
	);
}
